import React, { useEffect, useMemo, useState } from "react";
import type {
  CompanyDetailResponse,
  CompanyInfoResponse,
  UpdateCompanyInfoRequest,
} from "../../../../shared/types/api";
import { adminCompanyApiService } from "../../../../infrastructure/api/admin/admin-company-api-service";
import { bankTransferAutoConfirmAdminApiService } from "../../../../infrastructure/api/admin/bank-transfer-auto-confirm-admin-api-service";
import type { AdminAutoConfirmCompany } from "../../../../domain/types/bank-transfer-auto-confirm";

/**
 * 회사 상세 — 회사 정보 탭 (어드민 편집).
 *
 * 회사 관리자(MES) 의 "회사 정보" 편집과 동일한 필드를 어드민이 직접 수정한다.
 * 단 어드민 페이지이므로 **편집 버튼을 누르기 전엔 모든 필드 비활성**, **저장 버튼을 눌러야만 반영**된다.
 * 저장은 어드민 전용 `adminCompanyApiService.updateCompanyInfo`(PUT /api/admin/company/{id}, multipart) 를 쓴다.
 *
 * 파일 다운로드는 백엔드가 내려주는 `*DownloadUrl`(presigned, Content-Disposition: attachment) 로 이동만 하면
 * S3 가 강제 다운로드로 응답한다. 별도 blob fetch/창 열기 불필요.
 */

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}. ${match[2]}. ${match[3]}.`;
  return value;
};

/** 편집 가능한 텍스트 필드들(문자열 폼 상태로 관리). */
interface FormState {
  companyName: string;
  ceoName: string;
  domain: string;
  phone: string;
  secondaryContactType: string;
  secondaryContact: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  businessNumber: string;
  businessType: string;
  businessCategory: string;
  ecommerceBizNumber: string;
  additionalBusinessInfo: string;
  homeTaxKey: string;
  channelTalkKey: string;
  businessHours: string;
  lunchBreak: string;
  businessHoursNotice: string;
}

const toForm = (c: CompanyInfoResponse): FormState => ({
  companyName: c.companyName ?? "",
  ceoName: c.ceoName ?? "",
  domain: c.domain ?? "",
  phone: c.phone ?? "",
  secondaryContactType: c.secondaryContactType ?? "",
  secondaryContact: c.secondaryContact ?? "",
  zipCode: c.zipCode ?? "",
  address: c.address ?? "",
  addressDetail: c.addressDetail ?? "",
  businessNumber: c.businessNumber ?? "",
  businessType: c.businessType ?? "",
  businessCategory: c.businessCategory ?? "",
  ecommerceBizNumber: c.ecommerceBizNumber ?? "",
  additionalBusinessInfo: c.additionalBusinessInfo ?? "",
  homeTaxKey: c.homeTaxKey ?? "",
  channelTalkKey: c.channelTalkKey ?? "",
  businessHours: c.businessHours ?? "",
  lunchBreak: c.lunchBreak ?? "",
  businessHoursNotice: c.businessHoursNotice ?? "",
});

const inputCls = (editing: boolean): string =>
  `border rounded-lg px-3 py-2 text-[13px] outline-none w-full ${
    editing
      ? "border-[#D9D9D9] bg-white focus:border-[#4285F4]"
      : "border-[#EEEEEE] bg-[#FAFAFA] text-[#555] cursor-default"
  }`;

/** 라벨 + 입력 한 칸. */
const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  editing: boolean;
  required?: boolean;
  placeholder?: string;
  textarea?: boolean;
  maxLength?: number;
}> = ({
  label,
  value,
  onChange,
  editing,
  required,
  placeholder,
  textarea,
  maxLength,
}) => (
  <div>
    <label className="text-[12px] text-[#888] block mb-1">
      {label}
      {required && <span className="text-[#F24A4A]"> *</span>}
    </label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editing}
        rows={2}
        maxLength={maxLength}
        placeholder={editing ? placeholder : undefined}
        className={`${inputCls(editing)} resize-none`}
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editing}
        placeholder={editing ? placeholder : undefined}
        className={inputCls(editing)}
      />
    )}
  </div>
);

/** 라벨-값 한 행 (읽기 전용 시트). */
const SheetRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center py-2.5 border-b border-[#F0F0F0] last:border-b-0">
    <span className="w-[110px] flex-shrink-0 text-[12px] text-[#888]">
      {label}
    </span>
    <span className="text-[13px] font-medium min-w-0 break-words">
      {children}
    </span>
  </div>
);

const textOrDash = (value?: string | null): string => value?.trim() || "—";

/** 파일 업로드 칸 (현재 파일 미리보기/링크 + 새 파일 선택). */
const FileField: React.FC<{
  label: string;
  currentUrl?: string | null;
  file: File | null;
  onPick: (f: File | null) => void;
  editing: boolean;
  image?: boolean;
  /** presigned 다운로드 URL(attachment). 있으면 다운로드 아이콘을 노출한다. */
  downloadUrl?: string | null;
  /** accept 기본값 오버라이드. 파비콘처럼 image/* 로 안 잡히는 확장자가 있을 때 쓴다. */
  accept?: string;
}> = ({ label, currentUrl, file, onPick, editing, image, downloadUrl, accept }) => {
  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div>
      <label className="text-[12px] text-[#888] block mb-1">{label}</label>
      <div className="flex items-center gap-3">
        {image ? (
          <div className="w-[64px] h-[64px] rounded-lg border border-[#EEEEEE] bg-[#FAFAFA] flex items-center justify-center overflow-hidden flex-shrink-0">
            {previewUrl || currentUrl ? (
              <img
                src={previewUrl ?? currentUrl ?? ""}
                alt={label}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <span className="text-[10px] text-[#BBB]">없음</span>
            )}
          </div>
        ) : currentUrl ? (
          <a
            href={currentUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[12px] text-[#4285F4] hover:underline flex-shrink-0"
          >
            현재 파일 보기
          </a>
        ) : (
          <span className="text-[12px] text-[#BBB] flex-shrink-0">없음</span>
        )}
        <div className="min-w-0">
          <input
            type="file"
            accept={accept ?? (image ? "image/*" : "image/*,.pdf")}
            disabled={!editing}
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
            className="text-[12px] disabled:opacity-50 max-w-full"
          />
          {file && (
            <p className="text-[11px] text-[#4285F4] mt-0.5 truncate">
              {file.name}
            </p>
          )}
        </div>
        {downloadUrl && (
          <a
            href={downloadUrl}
            download
            title="다운로드"
            aria-label={`${label} 다운로드`}
            className="ml-auto flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#999] hover:text-[#4285F4] hover:bg-[#F4F7FF] transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
};

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="border border-[#D9D9D9] rounded-[10px] p-4">
    <h3 className="text-[14px] font-semibold mb-3">{title}</h3>
    {children}
  </div>
);

interface CompanyInfoTabProps {
  detail: CompanyDetailResponse | null;
  /** 저장 성공 후 부모가 상세를 재조회하도록 알린다. */
  onUpdated?: () => void | Promise<void>;
}

const CompanyInfoTab: React.FC<CompanyInfoTabProps> = ({ detail, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [sealFile, setSealFile] = useState<File | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  /**
   * 무통장입금 자동확인. 회사가 직접 신청하므로 어드민이 열어줄 것은 없고,
   * 자동으로 잡힌 연동 회원 아이디가 어긋났을 때 고치는 칸만 필요하다.
   * 저장은 회사 정보와 같은 [handleSave] 에서 함께 나간다 — 편집 버튼 하나로 묶는다.
   */
  const [autoConfirm, setAutoConfirm] = useState<AdminAutoConfirmCompany>();
  const [memberId, setMemberId] = useState("");

  // detail 이 바뀌면(최초 로드/저장 후 재조회) 편집 중이 아닐 때 폼을 동기화한다.
  useEffect(() => {
    if (!editing && detail?.company) setForm(toForm(detail.company));
  }, [detail, editing]);

  const companyId = detail?.company.id ?? null;
  useEffect(() => {
    if (companyId === null) return;
    let alive = true;
    void (async () => {
      try {
        const view = await bankTransferAutoConfirmAdminApiService.companyView(companyId);
        if (!alive) return;
        setAutoConfirm(view);
        setMemberId(view.memberId ?? "");
      } catch {
        // 자동확인 현황을 못 읽어도 회사 정보 편집은 계속돼야 한다
        if (alive) setAutoConfirm(undefined);
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId]);

  if (!detail) {
    return (
      <div className="py-10 text-center text-[12px] text-[#888]">
        회사 정보를 불러오는 중…
      </div>
    );
  }

  const { company, companyAdmin } = detail;
  const f = form ?? toForm(company);
  const setField = (key: keyof FormState) => (v: string) =>
    setForm((prev) => ({ ...(prev ?? toForm(company)), [key]: v }));

  const startEdit = () => {
    setError(null);
    setForm(toForm(company));
    setMemberId(autoConfirm?.memberId ?? "");
    setLogoFile(null);
    setSealFile(null);
    setCertFile(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setError(null);
    setForm(toForm(company));
    setMemberId(autoConfirm?.memberId ?? "");
    setLogoFile(null);
    setSealFile(null);
    setCertFile(null);
    setEditing(false);
  };

  const nullable = (v: string): string | null => (v.trim() === "" ? null : v.trim());

  const handleSave = async () => {
    if (!f.companyName.trim()) {
      setError("회사명은 필수입니다.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const request: UpdateCompanyInfoRequest = {
        companyId: company.id,
        companyAdminId: company.companyAdminId,
        companyName: f.companyName.trim(),
        ceoName: nullable(f.ceoName),
        domain: nullable(f.domain),
        phone: nullable(f.phone),
        secondaryContactType: nullable(f.secondaryContactType),
        secondaryContact: nullable(f.secondaryContact),
        zipCode: nullable(f.zipCode),
        address: nullable(f.address),
        addressDetail: nullable(f.addressDetail),
        ecommerceBizNumber: nullable(f.ecommerceBizNumber),
        additionalBusinessInfo: nullable(f.additionalBusinessInfo),
        businessNumber: nullable(f.businessNumber),
        businessType: nullable(f.businessType),
        businessCategory: nullable(f.businessCategory),
        homeTaxKey: nullable(f.homeTaxKey),
        channelTalkKey: nullable(f.channelTalkKey),
        businessHours: nullable(f.businessHours),
        lunchBreak: nullable(f.lunchBreak),
        businessHoursNotice: nullable(f.businessHoursNotice),
        // 이 탭에서 편집하지 않는 값은 기존 값을 보존해 덮어쓰기 방지.
        tossPayKey: company.tossPayKey ?? null,
        logoFile: logoFile ?? undefined,
        faviconFile: faviconFile ?? undefined,
        companySealImageFile: sealFile ?? undefined,
        businessRegistrationFile: certFile ?? undefined,
      };
      await adminCompanyApiService.updateCompanyInfo(request);
      // 바뀐 값이 있을 때만 부른다. 회사 정보와 저장처가 다르다
      const nextMemberId = memberId.trim();
      if (
        autoConfirm?.applied &&
        nextMemberId &&
        nextMemberId !== (autoConfirm.memberId ?? "")
      ) {
        const view = await bankTransferAutoConfirmAdminApiService.updateMemberId(
          company.id,
          nextMemberId,
        );
        setAutoConfirm(view);
        setMemberId(view.memberId ?? "");
      }
      setEditing(false);
      setLogoFile(null);
      setSealFile(null);
      setCertFile(null);
      await onUpdated?.();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "저장에 실패했습니다. 다시 시도해주세요.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5">
      {/* 헤더: 제목 + 편집/저장/취소 */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[16px] font-bold">회사 정보</h2>
          <p className="text-[11.5px] text-[#888] mt-0.5">
            편집을 누르면 수정할 수 있고, 저장을 눌러야 반영됩니다.
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="rounded-lg px-4 py-2 text-[13px] font-medium bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg px-4 py-2 text-[13px] font-medium bg-[#252525] text-white hover:bg-[#333] disabled:opacity-60"
              >
                {saving ? "저장 중…" : "저장"}
              </button>
            </>
          ) : (
            <button
              onClick={startEdit}
              className="rounded-lg px-4 py-2 text-[13px] font-medium bg-white text-[#252525] border border-[#252525] hover:bg-[#F5F5F5]"
            >
              편집
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 text-[12.5px] text-[#F24A4A] bg-[#FEF2F2] border border-[#F24A4A]/20 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="기본 정보">
          <div className="grid grid-cols-1 gap-3">
            <Field label="회사명" value={f.companyName} onChange={setField("companyName")} editing={editing} required />
            <Field label="대표자명" value={f.ceoName} onChange={setField("ceoName")} editing={editing} />
            <Field label="도메인" value={f.domain} onChange={setField("domain")} editing={editing} placeholder="example.com" />
            <Field label="연락처" value={f.phone} onChange={setField("phone")} editing={editing} placeholder="02-000-0000" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="보조 연락처 유형" value={f.secondaryContactType} onChange={setField("secondaryContactType")} editing={editing} placeholder="휴대폰/팩스 등" />
              <Field label="보조 연락처" value={f.secondaryContact} onChange={setField("secondaryContact")} editing={editing} />
            </div>
          </div>
        </Card>

        <Card title="소유자 정보">
          <p className="text-[11px] text-[#888] mb-1 -mt-1">
            회사 관리자(COMPANY_ADMIN) 계정 정보 (편집 불가).
          </p>
          <SheetRow label="이름">{textOrDash(companyAdmin.name)}</SheetRow>
          <SheetRow label="이메일">
            {companyAdmin.email ? (
              <a href={`mailto:${companyAdmin.email}`} className="text-[#4285F4] hover:underline">
                {companyAdmin.email}
              </a>
            ) : (
              "—"
            )}
          </SheetRow>
          <SheetRow label="휴대폰 번호">{textOrDash(companyAdmin.mobile)}</SheetRow>
          <SheetRow label="가입일">{formatDate(companyAdmin.createdAt)}</SheetRow>
        </Card>
        <Card title="사업자 정보">
          <div className="grid grid-cols-1 gap-3">
            <Field label="사업자번호" value={f.businessNumber} onChange={setField("businessNumber")} editing={editing} placeholder="0000000000" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="업태" value={f.businessType} onChange={setField("businessType")} editing={editing} />
              <Field label="업종" value={f.businessCategory} onChange={setField("businessCategory")} editing={editing} />
            </div>
            <Field label="통신판매업 번호" value={f.ecommerceBizNumber} onChange={setField("ecommerceBizNumber")} editing={editing} />
            {/* 쇼핑몰 푸터 표시 전용 자유 텍스트. 시스템 고유값인 사업자번호와 무관하다 */}
            <Field label="추가 사업자 정보 (푸터 표시용)" value={f.additionalBusinessInfo} onChange={setField("additionalBusinessInfo")} editing={editing} textarea maxLength={500} placeholder="예) 서충지점 | 상호 : OOO | 사업자등록번호 : 000-00-00000" />
          </div>
        </Card>

        <Card title="주소">
          <div className="grid grid-cols-1 gap-3">
            <Field label="우편번호" value={f.zipCode} onChange={setField("zipCode")} editing={editing} placeholder="00000" />
            <Field label="주소" value={f.address} onChange={setField("address")} editing={editing} />
            <Field label="상세주소" value={f.addressDetail} onChange={setField("addressDetail")} editing={editing} />
          </div>
        </Card>

        <Card title="연동 / 영업시간">
          <div className="grid grid-cols-1 gap-3">
            {/* 홈택스 키·채널톡 키는 회사 플러그인(내 회사 정보 > 추가 서비스 연동)으로 옮겨 여기서 편집하지 않는다. 백엔드도 이 값을 무시한다 */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="영업시간" value={f.businessHours} onChange={setField("businessHours")} editing={editing} placeholder="09:00-18:00" />
              <Field label="점심시간" value={f.lunchBreak} onChange={setField("lunchBreak")} editing={editing} placeholder="12:00-13:00" />
            </div>
            <Field label="영업시간 공지" value={f.businessHoursNotice} onChange={setField("businessHoursNotice")} editing={editing} textarea />
          </div>
        </Card>

        <Card title="무통장입금 자동확인">
          {autoConfirm?.applied ? (
            <>
              <Field
                label="연동 회원 아이디"
                value={memberId}
                onChange={setMemberId}
                editing={editing}
              />
              <p className="text-[11px] text-[#888] mt-1.5 leading-5">
                신청할 때 자동으로 확보합니다. 이 값이 어긋나면 입금 조회가 통째로
                실패하므로, 담당자 계정이 여럿이라 잘못 잡혔다면 여기서 고쳐 주세요.
              </p>
            </>
          ) : (
            <p className="text-[11px] text-[#888] leading-5">
              아직 신청하지 않은 회사입니다. 회사가 직접 신청하면 즉시 이용 상태가 됩니다 —
              어드민이 열어줄 것은 없습니다.
            </p>
          )}
        </Card>

        <Card title="파일">
          <div className="grid grid-cols-1 gap-4">
            <FileField label="로고 이미지" currentUrl={company.logoUrl} file={logoFile} onPick={setLogoFile} editing={editing} image downloadUrl={company.logoDownloadUrl} />
            <FileField label="파비콘" currentUrl={company.faviconUrl} file={faviconFile} onPick={setFaviconFile} editing={editing} image accept=".png,.ico,.svg,image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml" downloadUrl={company.faviconDownloadUrl} />
            <FileField label="회사 인감" currentUrl={company.companySealImageUrl} file={sealFile} onPick={setSealFile} editing={editing} image downloadUrl={company.companySealImageDownloadUrl} />
            <FileField label="사업자등록증" currentUrl={company.businessRegistrationFileUrl} file={certFile} onPick={setCertFile} editing={editing} downloadUrl={company.businessRegistrationFileDownloadUrl} />
          </div>
        </Card>

      </div>

    </div>
  );
};

export default CompanyInfoTab;
